/* The top-bar bell (replaces the old dead button): an unread badge and a drop-down of the
 * user's in-app alerts. Clicking an alert opens its detail (and marks it read); "Mark all
 * read" clears the badge. Open/close + outside-click follow components/ThemeSwitcher. */
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { useInboxStore } from './inboxStore';
import { useAlertPopupSettings, needsAck } from './alertPopupSettings';
import { SeverityChip, AckChip } from './AlertChips';
import './inbox.css';

export function AlertBell() {
  const unread = useInboxStore((s) => s.unread);
  const open = useInboxStore((s) => s.bellOpen);
  const list = useInboxStore((s) => s.list);
  const total = useInboxStore((s) => s.listTotal);
  const page = useInboxStore((s) => s.listPage);
  const loading = useInboxStore((s) => s.listLoading);
  const setOpen = useInboxStore((s) => s.setBellOpen);
  const settings = useAlertPopupSettings();
  const { confirm } = useConfirm();
  const notify = useNotify();
  const ref = useRef<HTMLDivElement>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on a click outside (the ThemeSwitcher pattern — a mouse listener, not a key one).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, setOpen]);

  // Popup-menu keyboard standard (rule 14): focus moves INTO the menu when it opens. The list
  // arrives async, so this re-runs as it fills — but never pulls focus from inside the menu.
  useEffect(() => {
    if (!open || menuRef.current?.contains(document.activeElement)) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open, list.length]);

  /** ↑/↓ move between alerts (Home/End jump), Esc closes and returns focus to the bell. */
  function onMenuKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = Math.min(items.length - 1, at + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, at - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); triggerRef.current?.focus(); return; }
    const target = next >= 0 ? items[next] : undefined;
    if (target) { e.preventDefault(); e.stopPropagation(); target.focus(); }
  }

  async function markAll() {
    const ok = await confirm({ title: 'Mark all read', message: 'Mark all your alerts as read?' });
    if (!ok) return;
    try {
      const n = await useInboxStore.getState().markAllRead();
      notify.success(n > 0 ? `Marked ${n} alert${n === 1 ? '' : 's'} as read.` : 'No unread alerts.');
    } catch {
      notify.error('Could not mark the alerts as read. Please retry.');
    }
  }

  const label = unread > 0 ? `Alerts — ${unread} unread` : 'Alerts';
  return (
    <div className="ia-bell-wrap" ref={ref}>
      <button ref={triggerRef} type="button" className="tb-btn tb-iconlink ia-bell" title={label} aria-label={label} aria-haspopup="menu"
        aria-expanded={open} onClick={() => setOpen(!open)}>
        <i className="ti ti-bell" />
        {unread > 0 && <span className="ia-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div ref={menuRef} className="ia-menu" role="menu" aria-label="Alerts" onKeyDown={onMenuKeyDown}>
          <div className="ia-menu-hd">
            <span>Alerts<span className="ia-count">{unread} unread</span></span>
            <button type="button" className="ca-btn" disabled={unread === 0} onClick={() => void markAll()}>
              <i className="ti ti-checks" /> Mark all read
            </button>
          </div>
          <div className="ia-menu-list">
            {list.length === 0 && (
              <div className="ia-empty">{loading ? 'Loading…' : 'No alerts yet. Permit-limit alerts for your role appear here.'}</div>
            )}
            {list.map((a) => (
              <button key={a.id} type="button" role="menuitem" className={`ia-item${a.readAt ? '' : ' unread'}`}
                onClick={() => useInboxStore.getState().openDetail(a.id)}>
                <div className="ia-item-top">
                  <SeverityChip severity={a.severity} />
                  <AckChip required={needsAck({ ackRequired: a.ackRequired, ackAt: null, popup: a.popup }, settings)} ackAt={a.ackAt} resolvedAt={a.resolvedAt} />
                  <span className="ia-item-time">{fmtLocalDateTime(a.createdAt)}</span>
                </div>
                <div className="ia-item-subj" title={a.subject}>{a.subject}</div>
                {a.summary && <div className="ia-item-sum">{a.summary}</div>}
              </button>
            ))}
          </div>
          {list.length < total && (
            <div className="ia-menu-ft">
              <button type="button" className="ca-btn" disabled={loading}
                onClick={() => void useInboxStore.getState().loadList(page + 1)}>
                Show more ({total - list.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
